import { Calculator, ShowErrorWay } from "../common/calculator";
import { DateField } from "../common/datetime_fields.js";
import { SummaryField } from "../components/summary_field/summary_field.js";
import { PeriodicDateField } from "../components/periodic_date_field/periodic_date_field.js";
import { DatePoint, distinctDates, makeDateFromAlertWorker } from '../js/datelib.js';

class VklCalculator extends Calculator {
    code() { return "vkl"; }
    type() { return "CalcVkl"; }

    initialize() {
        let self = this;      
        this.periodType = {
            Day: 1,
            Week: 2,
            Month: 3,
            Quarter: 4,
            Year: 5
        };

        this.patchFieldTypes([DateField, SummaryField, PeriodicDateField]);
        this.setupStatistic({
            calcName: 'vkl'
        });

        // add input fields
        this.depositSumField = this.addInputField('#depositSumInput input', 'depositSum', {
            type: 'money', options: { maxVal: 999999999999.99 },
            errors: [{ check: function (v) { return !!v; }, selector: '#depositSumInput .error-text' }],
            unsilence: ['rateBySum[].sum']
        });

        this.fixedRateField = this.addInputField('#rateTypeInput .fixed-rate', 'fixedRate', {
            type: 'float', options: { precision: 3 },
            errors: [{ check: function (v) { return !!v; }, selector: '#rateTypeInput .fixed-rate-error' }]
        });

        this.addInputField('#termDiv input', 'termValue', {
            type: 'count',
            errors: [
                { check: function (v) { return !!v; }, selector: '#termDiv .term-div .null-error' }
            ],
            unsilence: ['withdrawal[].summary', 'deposit[].summary']
        });

        this.addInputField('#termDiv date-picker', 'termDate', {
            errors: [
                { check: function (v) { return v.valid; }, selector: '#termDiv .date-div .null-error' },
                { check: function (v) { return !v.goesBefore(new DatePoint("01.01.1995")); }, selector: '#termDiv .date-div .from-bound-err' }
            ],
            unsilence: ['withdrawal[].summary', 'deposit[].summary', 'rateByTerm[].date']
        });

        this.dateTypeField = this.addInputField('#dateTypeInput', 'dateType', { 
            type: 'link', 
            options: { defaultGetValue: 'open' } ,
            unsilence: ['rateByTerm[].date']
        });

        // add group and dynamic fields
        this.rateBySumField = this.addInputField('#changing-sum-table', 'rateBySum', {
            dynamic: true,
            appendButtonSelector: "#addTermRateBySum",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: true,
            removeFirstEvent: function () {
                const $table = $("#changing-sum-table");
                $table.removeClass("err");
                $("#rateBySumDiv > .error-text-block > .no-start-error").removeAttr("style");
                const $list = $table.find(".dynamic-list-row");
                const $sum = $list.find(".dynamic-list-rate-sum input.base-input");
                $sum.val("");
                $sum.removeClass("err");
                const $input = $list.find(".dynamic-list-rate-percent input.base-input");
                $input.val("");
                $input.removeClass("err");

                $list.find(".error-text-block .error-text").removeAttr("style");
            },
            afterRemoval: function () {
                const $sum = $("#changing-sum-table").find(".dynamic-list-row").find(".dynamic-list-rate-sum input");
                self.submitForm($sum[0] || null, !self._submitOnChange);
            },
            ignoreForEmpty: false,
            fields: {
                sum: {
                    selector: ".dynamic-list-rate-sum input",
                    options: function ($clone) {
                        return {
                            type: 'money',
                            errors: [
                                {
                                    check: function (v, autoValidation) {return (autoValidation && $clone.find(".dynamic-list-rate-percent input").val() === '') || v !== null;},
                                    selector: $clone.find('.dynamic-row-error-sum')
                                },
                                {
                                    check: function (v) {
                                        return !v || !self.depositSum || $("#changing-sum-table .dynamic-list-row .dynamic-list-rate-sum input").map(function () {
                                            return new $(this).val();
                                        }).get().find(d => d !== '' && tools.parseLocaleFloat(d) <= self.depositSum) !== undefined;
                                    },
                                    selector: $("#rateBySumDiv > .error-text-block > .no-start-error"),
                                    errClassElem: "#changing-sum-table"
                                },
                                {
                                    check: function (v) { return v === null || !self.rateBySum || self.rateBySum.filter(r => r.sum === v).length === 0; },
                                    selector: $clone.find('.double-error')
                                },
                            ],
                            unsilence: ['rateBySum[].sum']
                        }
                    },
                },
                rate: {
                    selector: ".dynamic-list-rate-percent input",
                    options: function ($clone) {
                        return {
                            type: 'float',
                            options: { precision: 3 },
                            errors: [{ check: function (v) { return v != null; }, selector: $clone.find('.dynamic-row-error-rate') }]
                        }
                    },
                },
            }
        });

        this.rateByTermField = this.addInputField('#changing-rate-table', 'rateByTerm', {
            dynamic: true,
            appendButtonSelector: "#addRate",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: true,
            dontSubmitOnRemove: true,
            removeFirstEvent: function () {
                const $table = $("#changing-rate-table");
                $table.removeClass("err");
                $("#rateByTermDiv > .error-text-block > .no-start-error").removeAttr("style");
                const $list = $table.find(".dynamic-list-row");
                const $date = $list.find("date-picker");
                $date.val("");
                $date.removeClass("err");
                const $input = $list.find(".dynamic-list-rate-percent input.base-input");
                $input.val("");
                $input.removeClass("err");
                $list.find(".error-text-block .error-text").removeAttr("style");
                self.submitForm(null, !self._submitOnChange);
            },
            afterRemoval: function () {
                const $date = $("#changing-rate-table").find(".dynamic-list-row").find("date-picker");
                self.submitForm($date[0], !self._submitOnChange);
            },
            ignoreForEmpty: false,
            fields: {
                date: {
                    selector: ".dynamic-list-rate-date date-picker",
                    options: function ($clone) {
                        return {
                            showErrorWay: ShowErrorWay.SHOW_FIRST,
                            errors: [
                                {
                                    check: function (v, autoValidation) {return (autoValidation && $clone.find(".dynamic-list-rate-percent input").val() === '') || v.valid;},
                                    selector: $clone.find('.dynamic-row-error-date')
                                },
                                { check: function (v) { return !v.goesBefore(new DatePoint("01.01.1995")); }, selector: $clone.find('.from-bound-err') },
                                { check: function (v) { return !self.openDate || !v.goesBefore(self.openDate); }, selector: $clone.find('.before-start-err') },
                                {
                                    check: function (v) { return !v.valid || !self.rateByTerm || self.rateByTerm.filter(r => r.date && r.date.distanceTo(v) == 1).length === 0; },
                                    selector: $clone.find('.double-error')
                                },
                                {
                                    check: function (v) {
                                        return !v.valid || !(self.openDate && self.openDate.distanceTo(v) === 2 && !!$("#changing-rate-table .dynamic-list-row date-picker").map(function () {
                                            return new $(this).val();
                                        }).get().find(r => r && (new DatePoint(r)).distanceTo(self.openDate) == 1));
                                    },
                                    selector: $clone.find('.open-double-error')
                                },
                                {
                                    check: function (v) {
                                        return !v.valid || !self.openDate || !!$("#changing-rate-table .dynamic-list-row date-picker").map(function () {
                                            return new $(this).val();
                                        }).get().find(d => d && (!d.localeCompare(self.openDate.toString()) || !d.localeCompare(self.openDate.copy().inc().toString())));
                                    },
                                    selector: $("#rateByTermDiv > .error-text-block > .no-start-error"),
                                    errClassElem: "#changing-rate-table"
                                },
                            ],
                            unsilence: ['rateByTerm[].date']
                        }
                    },
                },
                rate: {
                    selector: ".dynamic-list-rate-percent input",
                    options: function ($clone) {
                        return {
                            type: 'float',
                            options: { precision: 3 },
                            errors: [{ check: function (v) { return v != null; }, selector: $clone.find('.dynamic-row-error-rate') }]
                        }
                    },
                },
            }
        });

        this.withdrawalField = this.addInputField('#withdrawalTable', 'withdrawal', {
            dynamic: true,
            appendButtonSelector: "#addWithdrawal",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: true,
            removeFirstEvent: function () {
                const $list = $("#withdrawalTable .dynamic-list-row");
                const $input = $list.find("summary-input");
                $input.val("");
                $list.removeClass("err");
                $($input[0].shadowRoot).find(".error-text").removeAttr("style");
            },
            ignoreForEmpty: false,
            fields: {
                summary: {
                    selector: "summary-input",
                    options: function ($clone) {
                        return {
                            type: 'summary',
                            showErrorWay: ShowErrorWay.SHOW_FIRST,
                            errors: SummaryField.getSummaryErrors($clone, self, 'openDate', 'closeDate'),
                            errClassElem: $clone
                        }
                    },
                },
            }
        });

        this.depositField = this.addInputField('#depositTable', 'deposit', {
            dynamic: true,
            appendButtonSelector: "#addDeposit",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: true,
            removeFirstEvent: function () {
                const $list = $("#depositTable .dynamic-list-row");
                const $input = $list.find("summary-input");
                $input.val("");
                $list.removeClass("err");
                $($input[0].shadowRoot).find(".error-text").removeAttr("style");
            },
            ignoreForEmpty: false,
            fields: {
                summary: {
                    selector: "summary-input",
                    options: function ($clone) {
                        return {
                            type: 'summary',
                            showErrorWay: ShowErrorWay.SHOW_FIRST,
                            errors: SummaryField.getSummaryErrors($clone, self, 'openDate', 'closeDate'),
                            errClassElem: $clone
                        }
                    },
                },
            }
        });

        this.fixedRateField.ignore = false;
        this.rateByTermField.ignore = true;
        this.setDynamicInputIgnore('rateByTerm', true);
        this.rateBySumField.ignore = true;
        this.setDynamicInputIgnore('rateBySum', true);
        this.withdrawalField.ignore = true;
        this.setDynamicInputIgnore('withdrawal', true);
        this.depositField.ignore = true;
        this.setDynamicInputIgnore('deposit', true);

        // add output fields
        this.addOutputField('.date-from-render', 'openDate', { type: 'dateFromRender', render: function (r) { return r ? r.toString() : null } });
        this.addOutputField('.date-to-render', 'closeDate', { type: 'dateToRender', render: function (r) { return r ? r.toString() : null } });
        this.addOutputField('.closure-date-render', 'closureDate', { type: 'closureDateRender', render: function (r) { return r ? r.toString() : null } });

        this.addOutputField('.periods-detalization', 'groupedDetalization', {
            html: true,
            render: function (groupPeriods) {
                function renderHeader(params) {
                    let inner = '';
                    if (params.isYear) {
                        inner += `<tr class="output-table-year-row"><td colspan="4" class="output-table-year-row-column"><span class="output-table-year-row-column__year">${params.year}</span><span class="output-table-year-row-column__days"> (${params.days} дней)</span></td>`;
                    }
                    if (params.isRate) {
                        inner += `<tr class="rate-info-row"><td colspan="4"><span>Ставка ${tools.getLocaleSum(params.rate)}%</span></td>`;
                    }
                    if (!params.hideColumns) {
                        inner += '<tr class="output-table-header-row"><td>период</td><td>дн.</td><td>проценты</td><td>вклад</td></tr>';
                    }

                    return inner;
                }

                function renderRow(period) {
                    period.period = period.span.toLocaleFormat();
                    return `<tr class="output-table-add-row"><td class="output-table-add-row-column__left">${period.period}</td>`
                        + `<td class="output-table-add-row-column__right">${period.days}</td>`
                        + `<td class="output-table-add-row-column__right" title="${period.formula}">${tools.getLocaleSum(period.percent)}</td>`
                        + `<td class="output-table-add-row-column__right">${tools.getLocaleSum(period.deposit)}</td></tr>`
                }

                function renderDecreaseIncreaseRow(period) {
                    return `<tr class="output-table-sum-data-row"><td class="output-table-sum-data-row-column__left">${period.stringDate}</td><td></td><td></td><td class="output-table-sum-data-row-column__right">${tools.getLocaleSum(period.deposit)}</td></tr>`
                        + `<tr class="output-table-sum-info-row"><td colspan="4" class="output-table-sum-info-row-column"><span>${(period.type !== 'withdrawal' ? 'Пополнения&nbsp;вклада' : 'Частичное&nbsp;снятие')}</span>`
                        + `<span class="output-table-sum-info-row-column-value--${(period.type !== 'withdrawal' ? 'increase' : 'decrease')}">${(period.type !== 'withdrawal' ? '+' : '&minus;')}${tools.getLocaleSumInRubbles(period.payment)}</span></td></tr>`
                        + `<tr class="output-table-sum-comment-row"><td colspan="6" class="output-table-sum-comment-row-column">${period.comment ? period.comment : ''}</td></tr>`;
                }

                const table = document.createElement('table');
                table.classList.add('output-table');
                const detalizationDiv = document.createElement('div');
                detalizationDiv.classList.add('detalization-div');
                let inner = "";
                let previousRate = 0;
                groupPeriods.forEach((periods, k) => {
                    let headerParams = {};
                    headerParams.year = periods[0].year;
                    headerParams.days = periods[0].daysInYear;
                    headerParams.isYear = true;
                    if (self.rateType.value !== 1 && periods[0].rate !== previousRate) {
                        headerParams.rate = periods[0].rate;
                        headerParams.isRate = true;
                    }

                    inner += renderHeader(headerParams);

                    for (let i = 0; i < periods.length; i++) {
                        headerParams = {};
                        if (periods[i] && periods[i].type === "add") {
                            if (i !== 0 && periods[i].rate !== periods[i - 1].rate) {
                                if (self.rateType.value !== 1) {
                                    headerParams.isRate = true;
                                    headerParams.rate = periods[i].rate;
                                }
                                headerParams.isYear = false;
                                inner += renderHeader(headerParams);
                            }
                            inner += renderRow(periods[i]);
                        } else {
                            inner += renderDecreaseIncreaseRow(periods[i]);
                        }
                        previousRate = periods[i].rate;
                    }
                });

                table.innerHTML = inner;
                detalizationDiv.append(table)

                return detalizationDiv.outerHTML;
            }
        });

        $('.excluded-funds-trigger').on('click', () => {
            const $this = $('.excluded-funds-trigger');
            const $notification = $('.excluded-funds-notification');
            if ($this.hasClass('hidden-notification')) {
                $this.text('Свернуть');
                $this.removeClass('hidden-notification');
                $notification.removeClass('hidden-control');
            } else {
                $this.text('Развернуть');
                $this.addClass('hidden-notification');
                $notification.addClass('hidden-control');
            }
        })

        this.loadDescription();

        this.submitOnClick('#calcSubmit');
        this.addResetButton("#clearCalcInput");
    }

    submitOnClick(selectorOrElem) {
        var self = this;
        $(selectorOrElem).click(function () {
            $(`summary-input`).each((i, el) => {el.closeWindow();});
            self.submitForm(null, false, true);
        });
    }

    isReady() {
        return true;
    }

    onReset() {
        $('.calc-div-results').removeClass('show-results');

        $('#isCapitalizationInput').removeClass("hidden-control");
        $('#rateTypeInput').removeClass("rate-type-2").removeClass("rate-type-3").addClass("rate-type-1");
        $("#rateTypeInput dropdown-select").attr('kind', "sharp-right");
        $('#withdrawalInnerDiv').addClass('hidden-control');
        $('#depositInnerDiv').addClass('hidden-control');
        
        this.fixedRateField.ignore = false;
        this.rateByTermField.ignore = true;
        this.setDynamicInputIgnore('rateByTerm', true);
        this.rateBySumField.ignore = true;
        this.setDynamicInputIgnore('rateBySum', true);
        this.withdrawalField.ignore = true;
        this.setDynamicInputIgnore('withdrawal', true);
        this.depositField.ignore = true;
        this.setDynamicInputIgnore('deposit', true);
    }

    generateExportObject() {
        let result = super.generateExportObject();
        result.groupedDetalization = tools.deepcopy(this.groupedDetalization);
        result.totalPercent = this.totalPercent;
        result.totalDeposit = this.totalDeposit;
        result.isExcess = this.isExcess;
        result.isClosure = this.isClosure;
        result.isShiftDate = this.isShiftDate;
        result.isExcludedFund = this.isExcludedFund;
        result.capitalizationEnabled = this.capitalizationEnabled;
        result.lastWithdrawal = this.lastWithdrawal;
        result.previousSum = this.previousSum;
        result.excludedFundMessage = this.excludedFundMessage;
        result.excludedFunds = this.excludedFunds;
        result.depositSum = this.depositSum;
        result.rateTypeValue = this.rateType.value;

        return result;
    }

    showResults() {
        super.showResults();

        var goalParams = {
            args: {
            },
            message: 'Is_Made'
        };
        this.sendStatisticMessage(goalParams);
    }

    get defaultFilled() {
        return ($("#debtDecreaseTable").find('.dynamic-list-row').length > 0) ||
            ($("#debtIncreaseTable").find('.dynamic-list-row').length > 0);
    }

    afterParse(sender, autoValidation) {
        let self = this;
        let $elem = $(sender);
        let $rateTypeInput = $("#rateTypeInput dropdown-select");
        let $rateTypeInputDiv = $("#rateTypeInput");
        let $periodicityInput = $("#periodicityInput dropdown-select");
        let $isCapitalizationInputDiv = $("#isCapitalizationInput");
        const $withdrawalCheckbox = $("#withdrawalDiv > .input-container-v1 > .checkboxrow");
        const $withdrawalInnerDiv = $("#withdrawalInnerDiv");
        const $depositCheckbox = $("#depositDiv > .input-container-v1 > .checkboxrow");
        const $depositInnerDiv = $("#depositInnerDiv");

        if ($elem.is($rateTypeInput)) {
            switch (this.rateType.value) {
                case 1:
                    $rateTypeInputDiv.removeClass("rate-type-2").removeClass("rate-type-3").addClass("rate-type-1");
                    $rateTypeInput.attr('kind', "sharp-right");
                    this.fixedRateField.ignore = false;
                    this.rateByTermField.ignore = true;
                    this.setDynamicInputIgnore('rateByTerm', true);
                    this.rateBySumField.ignore = true;
                    this.setDynamicInputIgnore('rateBySum', true);
                    break;
                case 2:
                    $rateTypeInputDiv.removeClass("rate-type-1").removeClass("rate-type-3").addClass("rate-type-2");
                    $rateTypeInput.attr('kind', "");
                    this.fixedRateField.ignore = true;
                    this.rateByTermField.ignore = true;
                    this.setDynamicInputIgnore('rateByTerm', true);
                    this.rateBySumField.ignore = false;
                    this.setDynamicInputIgnore('rateBySum', false);
                    break;
                case 3:
                    $rateTypeInputDiv.removeClass("rate-type-1").removeClass("rate-type-2").addClass("rate-type-3");
                    $rateTypeInput.attr('kind', "");
                    this.fixedRateField.ignore = true;
                    this.rateByTermField.ignore = false;
                    this.setDynamicInputIgnore('rateByTerm', false);
                    this.rateBySumField.ignore = true;
                    this.setDynamicInputIgnore('rateBySum', true);
                    break;
            }
        }
        else if ($elem.is($periodicityInput)) {
            if (this.periodicity.value === 2) {
                $isCapitalizationInputDiv.addClass("hidden-control");
            } else {
                $isCapitalizationInputDiv.removeClass("hidden-control");
            }
        }
        else if ($elem.is($withdrawalCheckbox)) {
            if (this.isWithdrawal) {
                $withdrawalInnerDiv.removeClass('hidden-control');
                this.withdrawalField.ignore = false;
                this.setDynamicInputIgnore('withdrawal', false);
            } else {
                $withdrawalInnerDiv.addClass('hidden-control');
                this.withdrawalField.ignore = true;
                this.setDynamicInputIgnore('withdrawal', true);
            }
        } else if ($elem.is($depositCheckbox)) {
            if (this.isDeposit) {
                $depositInnerDiv.removeClass('hidden-control');
                this.depositField.ignore = false;
                this.setDynamicInputIgnore('deposit', false);
            } else {
                $depositInnerDiv.addClass('hidden-control');
                this.depositField.ignore = true;
                this.setDynamicInputIgnore('deposit', true);
            }
        }

        const prevOpenDate = this.openDate && this.openDate.copy();
        if (this.termDate) {
            this.calculateTermDates();
        }

        if(this.openDate){
            $('.open-date').html(this.openDate.toLocaleFormat());
            $('.open-date-plus-one').html(this.openDate.copy().inc().toLocaleFormat());
        }

        if ((prevOpenDate ? !this.openDate : !!this.openDate) || prevOpenDate && prevOpenDate.distanceTo(this.openDate) !== 1){
            this.parse($("#termDiv date-picker")[0], autoValidation);
        }
            
        // Combined error for changingRate  
        function proceedTwoErrors($error1, $error2, $error3, $error4) {
            if ($error4[0]) {
                $error2.css('display', 'block');
                $error3.removeAttr("style");
                if ($error4[0].innerHTML.slice(-1) != ';')
                    $error4[0].innerHTML += ';';
            } else {
                $error1.removeAttr("style");
                $error2.removeAttr("style");
                $error3.css('display', 'block');
            }
        }
        function updateChangingRateFields(tableSelector, Input1DivSelector, Input2DivSelector, err1Selector, err2Selector, err3Selector) {
            $(tableSelector).find(".dynamic-list-row").each((i, elem) => {
                const $elem = $(elem);
                const $firstInput = $elem.find(`${Input1DivSelector} .base-input`);
                const $secondInput = $elem.find(`${Input2DivSelector} .base-input`);
                const firstHasErr = $firstInput.hasClass('err');
                const secondHasErr = $secondInput.hasClass('err');
                const $error1 = $elem.find(err1Selector);
                const $error2 = $elem.find(err2Selector);
                const $error3 = $elem.find(err3Selector);
                const $error4 = $elem.find('.error-text:visible').filter((i, el) => [$error1[0], $error2[0], $error3[0]].indexOf(el) == -1);
                if (firstHasErr && secondHasErr) {
                    if ($firstInput.is("input"))
                        $firstInput.addClass("sharp-right");
                    else
                        $firstInput.attr('kind', "s-small transparent grip-right");
                    $secondInput.addClass("sharp-left");
                    proceedTwoErrors($error1, $error2, $error3, $error4)
                } else {
                    if ($firstInput.is("input"))
                        $firstInput.removeClass("sharp-right");
                    else
                        $firstInput.attr('kind', "s-small transparent");
                    $secondInput.removeClass("sharp-left");
                    $error3.removeAttr("style");
                    if (secondHasErr)
                        $error2.css('display', 'block');
                    if (firstHasErr) {
                        if ($error2.css('display') == 'none') {
                            if ($error4[0]) {
                                if ($error4[0].innerHTML.slice(-1) == ';')
                                    $error4[0].innerHTML = $error4[0].innerHTML.slice(0, -1);
                            } else
                                $error1.css('display', 'block');
                        } else
                            proceedTwoErrors($error1, $error2, $error3, $error4);
                    }
                }
            });
        }

        updateChangingRateFields('#rateByTermDiv', '.dynamic-list-rate-date', '.dynamic-list-rate-percent', '.dynamic-row-error-date', '.dynamic-row-error-rate', '.error-date-rate');
        updateChangingRateFields('#rateBySumDiv', '.dynamic-list-rate-sum', '.dynamic-list-rate-percent', '.dynamic-row-error-sum', '.dynamic-row-error-rate', '.error-sum-rate');
    }

    getNextDate(date, dayNumber = null, monthNumber = null, yearNumber = null, isShiftDate = false, dayOfDate = null, isNextDate = true) {
        if (yearNumber) {
            if (isNextDate) {
                date.addMonths(12 * yearNumber);
            } else {
                date.addMonths(-12 * yearNumber);
            }
        }

        if (monthNumber) {
            if (isNextDate) {
                date.addMonths(monthNumber);
            } else {
                date.addMonths(-monthNumber);
            }
        }

        if (dayNumber) {
            if (isNextDate) {
                date.inc(dayNumber);
            } else {
                date.inc(-dayNumber);
            }
        }

        if (dayOfDate) {
            date.setDateGently(dayOfDate, true);
        }

        if (isShiftDate && isNextDate) {
            while (true) {
                if (!date.isDayOff()) {
                    return date;
                }
                date.inc();
            }
        }

        return date;
    }

    getNextPayDate(date, period, multiplier = 1, isShiftDate = false, isNextDate = true) {
        let result;
        let currentDate = date.copy();
        switch (period) {
            case 1:
                result = this.getNextDate(currentDate, multiplier, null, null, isShiftDate, null, isNextDate);
                break;
            case 2:
                result = this.getNextDate(currentDate, multiplier * 7, null, null, isShiftDate, null, isNextDate);
                break;
            case 3:
                result = this.getNextDate(currentDate, null, multiplier, null, isShiftDate, null, isNextDate);
                break;
            case 4:
                result = this.getNextDate(currentDate, null, multiplier * 3, null, isShiftDate, null, isNextDate);
                break;
            case 5:
                result = this.getNextDate(currentDate, null, null, multiplier, isShiftDate, null, isNextDate);
                break;
        }
        return result;
    }

    calculateTermDates() {
        let firstDate = this.termDate.copy();
        const isOpenDate = this.dateType === 'open';
        let secondDate;
        let expectedSecondDate;
        if (this.termValue) {
            switch (this.termType.value) {
                case 1:
                    secondDate = this.getNextPayDate(firstDate, this.periodType.Day, this.termValue, this.isShiftDate, isOpenDate);
                    expectedSecondDate = this.getNextPayDate(firstDate, this.periodType.Day, this.termValue, false, isOpenDate);
                    break;
                case 2:
                    secondDate = this.getNextPayDate(firstDate, this.periodType.Week, this.termValue, this.isShiftDate, isOpenDate);
                    expectedSecondDate = this.getNextPayDate(firstDate, this.periodType.Week, this.termValue, false, isOpenDate);
                    break;
                case 3:
                    secondDate = this.getNextPayDate(firstDate, this.periodType.Month, this.termValue,  this.isShiftDate, isOpenDate);
                    expectedSecondDate = this.getNextPayDate(firstDate, this.periodType.Month, this.termValue, false, isOpenDate);
                    break;
                case 4:
                    secondDate = this.getNextPayDate(firstDate, this.periodType.Year, this.termValue,  this.isShiftDate, isOpenDate);
                    expectedSecondDate = this.getNextPayDate(firstDate, this.periodType.Year, this.termValue, false, isOpenDate);
                    break;
            }
        }

        if (isOpenDate) {
            this.openDate = firstDate;
            this.closeDate = secondDate;
            this.expectedCloseDate = expectedSecondDate;
        } else {
            this.openDate = secondDate;
            this.expectedCloseDate = firstDate.copy();
            if (this.isShiftDate) {
                while (true) {
                    if (!firstDate.isDayOff()) {
                        break;
                    }
                    firstDate.inc();
                }
            }
            this.closeDate = firstDate.copy();
        }
    }

    handleFunds(data, result, dates, type, excludedFunds, expectedCloseDate) {
        let openDate = dates[0];
        let closeDate = expectedCloseDate ? expectedCloseDate : dates[dates.length - 1];
        let array = [];
        for (let i = 0; i < data.length; i++) {
            const dataItem = data[i];
            if(!dataItem.summary.payment) {
                continue;
            }
            if (dataItem.summary.repeat === "no") {
                const summaryDate = dataItem.summary.date.copy();
                if (dataItem.summary.onlyWorkdays) {
                    while (true) {
                        if (!summaryDate.isDayOff()) {
                            break;
                        }
                        summaryDate.inc();
                    }
                }
                if (closeDate.goesBefore(summaryDate)){
                    excludedFunds.push({
                        date: summaryDate,
                        sum: dataItem.summary.payment,
                        type: type,
                        comment: dataItem.summary.name,
                    });
                    continue;
                }
                dates.push(summaryDate);
                array.push({
                    date: summaryDate,
                    type: type,
                    payment: dataItem.summary.payment,
                    year: summaryDate.date.getFullYear(),
                    comment: dataItem.summary.name,
                });
            } else {
                let monthCount = 1;
                let dayOfMonth = dataItem.summary.day !== -1 ? dataItem.summary.day : 31;
                let firstDate = (dataItem.summary.wholePeriod ? openDate : dataItem.summary.dateFrom).copy();
                if (firstDate.date.getDate() > dayOfMonth) {
                    firstDate = this.getNextDate(firstDate.copy(), null, 1, null, dataItem.summary.onlyWorkdays, dayOfMonth);
                } else {
                    firstDate.setDateGently(dayOfMonth, true);
                }
                
                if (dataItem.summary.onlyWorkdays){
                    while (true) {
                        if (!firstDate.isDayOff()) {
                            break;
                        }
                        firstDate.inc();
                    }
                }  

                if (!firstDate.goesBefore(closeDate)) {
                    excludedFunds.push({
                        date: dataItem.summary.dateFrom,
                        sum: dataItem.summary.payment,
                        type: type,
                        comment: dataItem.summary.name,
                    });
                    continue;
                }                          

                if (openDate.goesBefore(firstDate)) {
                    array.push({
                        date: firstDate,
                        type: type,
                        payment: dataItem.summary.payment,
                        year: firstDate.date.getFullYear(),
                        comment: dataItem.summary.name,
                    });
                    dates.push(firstDate);
                }

                let lastDate = (!dataItem.summary.wholePeriod && dataItem.summary.dateTo.goesBefore(closeDate))
                    ? dataItem.summary.dateTo.copy()
                    : closeDate.copy();

                while (true) {
                    let nextDate = this.getNextDate(firstDate.copy(), null, monthCount, null, dataItem.summary.onlyWorkdays, dayOfMonth);
                    if (lastDate.goesBefore(nextDate)) {
                        break;
                    }
                    dates.push(nextDate);
                    array.push({ 
                        date: nextDate, 
                        type: type, 
                        payment: dataItem.summary.payment, 
                        year: nextDate.date.getFullYear(),
                        comment: dataItem.summary.name,
                    });                   
                    if (monthCount) {
                        monthCount++;
                    }
                }
            }
        }

        while (array.length > 0) {
            let item = array.shift();
            let equalItems = array.filter(x => x.date.isEqualTo(item.date));
            let fullPayment = item.payment;
            for (let i = 0; i < equalItems.length; i++) {
                fullPayment += equalItems[i].payment;
            }
            result.push({ 
                date: item.date, 
                type: type, 
                payment: fullPayment, 
                year: item.date.date.getFullYear(),
                excluded: true,
                comment: item.comment
            });
            array = array.filter(x => !x.date.isEqualTo(item.date));
        }
    }

    calculate(params) {
        if (params.autoValidation) {
            return false;
        }

        function getDaysInYear(date) {
            return date.fixDayMonthYear(1, 1).distanceTo(date.fixDayMonthYear(31, 12));
        }

        function getRateBySum(sum, ratesBySum) {
            for (let i = ratesBySum.length - 1; i >= 0; i--) {
                if (sum >= ratesBySum[i].sum) {
                    return ratesBySum[i].rate;
                }
            }
            return 0;
        }

        function getRateByTerm(date, ratesByTerm) {
            for (let i = ratesByTerm.length - 1; i >= 0; i--) {
                if (!date.goesBefore(ratesByTerm[i].date)) {
                    return ratesByTerm[i].rate;
                }
            }
            return 0;
        }

        function addEndOfYearDates(dates) {
            var dateFrom = dates[0].copy();
            var dateTo = dates[dates.length - 1].copy();
            for (var i = dateFrom.date.getFullYear(); i < dateTo.date.getFullYear(); i++) {
                var date = new DatePoint('31.12.' + i);
                dates.push(date);
            }

            dates = distinctDates(dates);
            dates.sort((a, b) => a.goesBefore(b) ? -1 : 1);

            return dates;
        }

        function removeDatesBeforeOpenDate(dates, openDate) {
            return dates.filter(x => !x.goesBefore(openDate));
        }


        this.calculateTermDates();

        let dates = [];
        const payDates = [];
        let nextDate;
        let multiplier = 1;
        dates.push(this.openDate.copy());
        payDates.push(this.openDate.copy());
        while (true) {
            switch (this.periodicity.value) {
                case 1:
                    nextDate = this.getNextPayDate(this.openDate, this.periodType.Day, multiplier, this.isShiftDate);
                    break;
                case 3:
                    nextDate = this.getNextPayDate(this.openDate, this.periodType.Month, multiplier, this.isShiftDate);
                    break;
                case 4:
                    nextDate = this.getNextPayDate(this.openDate, this.periodType.Quarter, multiplier, this.isShiftDate);
                    break;
                case 5:
                    nextDate = this.getNextPayDate(this.openDate, this.periodType.Year, multiplier, this.isShiftDate);
                    break;
            }
            if (this.periodicity.value === 2 || !nextDate.goesBefore(this.closeDate)) {
                break;
            }
            dates.push(nextDate);
            payDates.push(nextDate);
            multiplier++;
        }
        dates.push(this.closeDate.copy());
        payDates.push(this.closeDate.copy());

        this.finalRatesByTerm = [];
        switch (this.rateType.value) {
            case 1:
                break;
            case 2:
                this.rateBySum.sort((a, b) => a.sum - b.sum);
                break;
            case 3:
                this.rateByTerm.forEach(x => {
                    if (!this.expectedCloseDate || (this.expectedCloseDate && !this.expectedCloseDate.goesBefore(x.date))) {
                        this.finalRatesByTerm.push(x);
                    }
                });
                this.finalRatesByTerm.sort((a, b) => a.date.goesBefore(b.date) ? -1 : 1);
                let index = 0;
                while (this.finalRatesByTerm[index] && !this.closeDate.goesBefore(this.finalRatesByTerm[index].date)) {
                    dates.push(this.finalRatesByTerm[index].date.copy().dec());
                    index++;
                }
                dates = distinctDates(dates);
                dates.sort((a, b) => a.goesBefore(b) ? -1 : 1);
                break;
        }

        this.excludedFunds = [];
        const withdrawals = [];
        if (this.isWithdrawal) {
            this.handleFunds(this.withdrawal.filter(w => !w.summary.defaultInputs), withdrawals, dates, 'withdrawal', this.excludedFunds, this.expectedCloseDate);
            dates = distinctDates(dates);
            dates.sort((a, b) => a.goesBefore(b) ? -1 : 1);
        }

        const deposits = [];
        if (this.isDeposit) {
            this.handleFunds(this.deposit.filter(d => !d.summary.defaultInputs), deposits, dates, 'deposit', this.excludedFunds, this.expectedCloseDate);
            dates = distinctDates(dates);
            dates.sort((a, b) => a.goesBefore(b) ? -1 : 1);
        }

        dates = addEndOfYearDates(dates);
        dates = removeDatesBeforeOpenDate(dates, this.openDate);

        let detalization = [];
        let dtFrom = dates.shift();
        let dtTo = dtFrom.copy();
        let currentSum = this.depositSum;
        let intervalSum = this.depositSum;
        let totalPercent = 0;
        let rate = 0;
        this.closureDate = null;
        this.previousSum = 0;
        this.lastWithdrawal = 0;
        this.isClosure = false;
        this.isExcess = false;
        this.capitalizationEnabled = this.isCapitalization && this.periodicity.value !== 2;
        while (dtFrom && !this.isClosure && !this.isExcess) {
            if (dates.length > 0) {
                dtFrom = dtFrom.inc();
                dtTo = dates[0].copy();
                const daysInYear = getDaysInYear(dtFrom);
                const days = dtFrom.distanceTo(dtTo);
                switch (this.rateType.value) {
                    case 1:
                        rate = this.fixedRate;
                        break;
                    case 2:
                        rate = getRateBySum(this.capitalizationEnabled ? intervalSum : currentSum, this.rateBySum)
                        break;
                    case 3:
                        rate = getRateByTerm(dtFrom, this.finalRatesByTerm)
                        break;
                }

                let percent = 0;
                let formula = '';
                if (this.capitalizationEnabled) {
                    percent = tools.roundNumber((intervalSum * (1 + ((rate / 100) * (days / daysInYear)))) - intervalSum, 2);
                    formula = `${tools.getLocaleSum(intervalSum)} × ((1 + ${tools.getLocaleSum(rate)} / 100 × (${days} / ${daysInYear}) ^ 1) − ${tools.getLocaleSum(intervalSum)}`;
                    currentSum = tools.roundNumber(currentSum + percent, 2);
                    const item = payDates.find(x => x.isEqualTo(dtTo));
                    if (item) {
                        intervalSum = currentSum;
                    }
                } else {
                    percent = tools.roundNumber(intervalSum * (rate / 100) * (days / daysInYear), 2);
                    formula = `${tools.getLocaleSum(intervalSum, 2)} × ${tools.getLocaleSum(rate)} / 100 × (${days} / ${daysInYear})`;
                }

                totalPercent += percent;

                detalization.push({
                    year: dtFrom.date.getFullYear(),
                    daysInYear: daysInYear,
                    span: dtFrom.getSpan(dtTo),
                    days: days,
                    rate: rate,
                    percent: percent,
                    formula: formula,
                    deposit: currentSum,
                    type: 'add',
                });

                if (this.isDeposit) {
                    const item = deposits.find(x => x.date.isEqualTo(dtTo));
                    if (item) {
                        item.excluded = false;
                        currentSum += item.payment;
                        intervalSum += item.payment;
                        item.stringDate = item.date.toLocaleFormat();
                        item.deposit = currentSum > 0 ? currentSum : 0;
                        item.rate = rate;
                        detalization.push(item);
                    }
                }

                if (this.isWithdrawal) {
                    const item = withdrawals.find(x => x.date.isEqualTo(dtTo));
                    if (item) {
                        item.excluded = false;
                        currentSum -= item.payment;
                        intervalSum -= item.payment;
                        if (intervalSum < 0) {
                            intervalSum = currentSum;
                        }
                        item.stringDate = item.date.toLocaleFormat();
                        item.deposit = currentSum > 0 ? currentSum : 0;
                        item.rate = rate;
                        detalization.push(item);
                    }
                }
            }

            dtFrom = dates.shift();
            this.closureDate = dtTo;
            const roundedCurrentSum = tools.roundNumber(currentSum, 2);
            if (roundedCurrentSum < 0) {
                this.previousSum = detalization[detalization.length - 2].deposit;
                this.lastWithdrawal = detalization[detalization.length - 1].payment;
                this.isExcess = true;
                currentSum = 0;
            } else if (roundedCurrentSum === 0) {
                this.isClosure = true;
                currentSum = 0;
            }
        }

        this.groupedDetalization = [];
        let arr = [];
        for (let i = 0; i < detalization.length; i++) {
            if (i !== 0 && detalization[i - 1].year !== detalization[i].year) {
                this.groupedDetalization.push(arr.slice());
                arr = [];
            }
            arr.push(detalization[i]);
        }
        this.groupedDetalization.push(arr.slice());

        this.totalPercent = totalPercent;
        this.totalDeposit = tools.roundNumber(currentSum, 2);

        let termVerb = "";
        switch (this.termType.value) {
            case 1:
                termVerb = tools.verboseCountingValue(this.termValue, [["день", "дня", "дней"]]);
                break;
            case 2:
                termVerb = tools.verboseCountingValue(this.termValue, [["неделя", "недели", "недель"]]);
                break;
            case 3:
                termVerb = tools.verboseCountingValue(this.termValue, [["месяц", "месяца", "месяцев"]]);
                break;
            case 4:
                termVerb = tools.verboseCountingValue(this.termValue, [["год", "года", "лет"]]);
                break;
        }

        this.mainPeriodString = `${this.openDate.toString()} – ${this.closeDate.toString()} (${termVerb})`;
        
        withdrawals.forEach(item => {
            if (item.excluded) {
                this.excludedFunds.push({
                    date: item.date,
                    sum: item.payment,
                    type: 'withdrawal',
                    comment: item.comment,
                });
            }
        });

        deposits.forEach(item => {
            if (item.excluded) {
                this.excludedFunds.push({
                    date: item.date,
                    sum: item.payment,
                    type: 'deposit',
                    comment: item.comment,
                });
            }
        });

        this.excludedFundMessage = '';
        this.excludedFundsNoteHTML = '';
        let excludedWithdrawalsHTML = '<p class="notification-header">Частичные снятия</p>';
        let excludedDepositsHTML = '<p class="notification-header">Пополнения вклада</p>';
        this.isExcludedFund = false;
        if (this.excludedFunds.length > 0) {
            this.isExcludedFund = true;
            this.excludedFunds.forEach(item => {
                item.date = item.date.toLocaleFormat();
                this.excludedFundMessage = `${(item.type === 'withdrawal' ? 'Частичное снятие' : 'Пополнение вклада')} ${item.date} на ${tools.getLocaleSumInRubbles(item.sum)} не участвует в расчёте.`;
                const noteMessage = `<p class="notification-row"><span>${item.date}</span><span>${tools.getLocaleSumInRubbles(item.sum)}</span></p>`
                    + (item.comment ? `<p class="notification-comment">${item.comment}</p>` : '');
                if (item.type === 'withdrawal') {
                    excludedWithdrawalsHTML += noteMessage;
                } else {
                    excludedDepositsHTML += noteMessage;
                }
            });
        }
        this.excludedFundsNoteHTML += `<div class="excludedFundsColumn">${excludedWithdrawalsHTML}</div><div class="excludedFundsColumn">${excludedDepositsHTML}</div>`;

        this.rateInfo = '';
        switch(this.rateType.value) {
            case 1:
                this.rateInfo += ` ${tools.getLocaleNumber(this.fixedRate, { minPrecision: 0, maxPrecision: 3 })}%`;
                break;
            case 2:
                this.rateBySum.forEach((item, index) => {
                    if(index !== 0){
                        this.rateInfo += ',';
                    }
                    this.rateInfo += ` от ${tools.getLocaleSumInRubbles(item.sum)} – ${tools.getLocaleNumber(item.rate, { minPrecision: 0, maxPrecision: 3 })}%`;
                });
                break;
            case 3:
                this.finalRatesByTerm.forEach((item, index) => {
                    if(index !== 0){
                        this.rateInfo += ',';
                    }
                    this.rateInfo += ` с ${item.date.toLocaleFormat()} – ${tools.getLocaleNumber(item.rate, { minPrecision: 0, maxPrecision: 3 })}%`;
                });
                break;
            default:
                break;
        }

        return true;
    }

    beforeRender() {
        $('.total-deposit').prop('title', tools.verboseNumber(this.totalDeposit));
        $('.total-percent').prop('title', tools.verboseNumber(this.totalPercent));

        $('.excluded-funds-trigger').text('Развернуть');
        $('.excluded-funds-trigger').addClass('hidden-notification');
        $('.excluded-funds-notification').addClass('hidden-control');
    }

    hideResults() {
        super.hideResults();
        this.sendStatisticMessage('Is_Made_With_Error');
    }

    renderError() {
        super.renderError();
        this.sendStatisticMessage('Is_Made_With_Error');
    }

    setDynamicInputIgnore(inputName, ignore) {
        this.inputFields.filter(f => !f.name.indexOf(inputName + "[]")).forEach(f => f.ignore = ignore);
    }
}

$(function () {
    var calc = new VklCalculator();
});